import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzText,
    BringzzOrderCard,
    BringzzButton,
    BringzzBasketProduct,
} from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';

const getStatusText = (status, t) => {
    const statusMap = {
        0: t('component.order_view.order_status.open'),
        1: t('component.order_view.order_status.matching_pharmacy'),
        2: t('component.order_view.order_status.pharmacy_matched'),
        3: t('component.order_view.order_status.being_prepared'),
        4: t('component.order_view.order_status.prepared'),
        5: t('component.order_view.order_status.shopper_matched'),
        6: t('component.order_view.order_status.shopper_arrived_at_customer'),
        7: t('component.order_view.order_status.shopper_identified_by_customer'),
        8: t('component.order_view.order_status.shopper_arrived_at_pharmacy'),
        9: t('component.order_view.order_status.shopper_identified_by_pharmacy'),
        10: t('component.order_view.order_status.order_confirmed_by_pharmacy'),
        11: t('component.order_view.order_status.order_confirmed_by_shopper'),
        12: t('component.order_view.order_status.shopper_arrived_with_package'),
        13: t('component.order_view.order_status.shopper_identified_with_package'),
        14: t('component.order_view.order_status.order_confirmed_by_shopper_with_package'),
        15: t('component.order_view.order_status.completed'),
        16: t('component.order_view.order_status.canceled'),
        17: t('component.order_view.order_status.error')
    };
    return statusMap[status] || t('component.order_view.order_status.unknown_status');
};

const formatDate = (dateString, t) => {
    if (!dateString) return t('component.order_view.date_not_available');
    try {
        // Parse date in format "DD.MM.YYYY HH:mm"
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('.');
        const [hours, minutes] = timePart.split(':');
        
        // Create date object (month is 0-based in JS Date)
        const date = new Date(year, month - 1, day, hours, minutes);
        
        // Return if invalid date
        if (isNaN(date.getTime())) {
            return t('component.order_view.invalid_date');
        }
        
        return date;
    } catch (e) {
        return t('component.order_view.invalid_date');
    }
};

const ConsumerOrderViewPage = () => {
    const { backPress } = useNavigation();
    const navigate = useNavigate();
    const { uuid } = useParams();
    const { sendRequest, loading } = useRequest();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [invoiceData, setInvoiceData] = useState(null);
    const { t } = useTranslation();

    const fetchInvoiceData = async () => {
        if (!order?.id || invoiceData) return;
        
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/getCustomerOrderInvoice',
                data: {
                    orderId: order.id
                }
            });
            
            if (response.status === 'success') {
                setInvoiceData(response.data);
            }
        } catch (error) {
            console.error('Error getting invoice:', error);
        }
    };

    const fetchOrder = async () => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/getOrder',
                data: {
                    orderUUID: uuid
                }
            });
            
            if (response.status === 'success') {
                setOrder(response.data);
            } else {
                setError(t('component.order_view.failed_to_load_order'));
                navigate('/consumer/orders');
            }
        } catch (error) {
            console.error(error);
            setError(t('component.order_view.failed_to_load_order'));
            navigate('/consumer/orders');
        }
    };

    useEffect(() => {
        if (uuid) {
            fetchOrder();
        } else {
            navigate('/consumer/orders');
        }
    }, [uuid]);

    useEffect(() => {
        if (order?.id && !invoiceData) {
            fetchInvoiceData();
        }
    }, [order]);

    // Loading state
    if (loading || !order) {
        return (
            <BringzzPageContainer
                className="bg-white"
                header={{
                    left: (
                        <BringzzIcon
                            size='22'
                            icon='IconChevronLeft'
                            folder='LineIcons'
                            className='cursor-pointer'
                            onClick={backPress}
                        />
                    ),
                    title: t('component.order_view.loading_order')
                }}
                authenticated={false}
            >
                <div className="flex items-center justify-center p-8">
                    <BringzzIcon
                        size="32"
                        icon="IconSpinner"
                        folder="LineIcons"
                        className="animate-spin text-magic-lilac"
                    />
                </div>
            </BringzzPageContainer>
        );
    }

    // Error state
    if (error) {
        return (
            <BringzzPageContainer
                className="bg-white"
                header={{
                    left: (
                        <BringzzIcon
                            size='22'
                            icon='IconChevronLeft'
                            folder='LineIcons'
                            className='cursor-pointer'
                            onClick={backPress}
                        />
                    ),
                    title: t('component.order_view.order_not_found')
                }}
                authenticated={false}
            >
                <div className="flex items-center justify-center p-8">
                    <BringzzText tag="h3" className="text-gray-500">{error}</BringzzText>
                </div>
            </BringzzPageContainer>
        );
    }

    const isCompleted = order && order.deliveryStatus >= 15;

    const handleDownloadInvoice = async () => {
        try {
            const response = await sendRequest({
                method: 'POST',
                url: '/module/order/downloadCustomerInvoicePdf',
                data: {
                    orderId: order.id
                }
            });
            
            if (response.status === 'success') {
                const link = document.createElement('a');
                link.href = response.data.content;
                link.download = response.data.filename;
                link.click();
            }
        } catch (error) {
            console.error('Error downloading invoice:', error);
        }
    };

    return (
        <BringzzPageContainer
            className="bg-white"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: `Order #${order.id}`
            }}
            authenticated={false}
        >
            <div className='px-6'>
                <div className='text-center my-4'>
                    <BringzzText tag='h1'>€ {order.totalPrice ? parseFloat(order.totalPrice).toFixed(2) : '0.00'}</BringzzText>
                    <BringzzText tag='h3'>{order.products?.length || 0} {t('component.order_view.items')}</BringzzText>
                    <BringzzText tag='h4' className="mt-2 text-gray-600">{getStatusText(order.deliveryStatus, t)}</BringzzText>
                </div>

                <div className='space-y-4'>
                    <div className='border-t pt-4'>
                        <BringzzText tag='h3' className="mb-2">{t('component.order_view.order_details')}</BringzzText>
                        <div className='bg-gray-50 rounded-lg p-4'>
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.pharmacy')}</BringzzText>
                                <BringzzText tag='h4'>{order.pharmacy?.name || order.pharmacyId_Dyn_Sort || (order.deliveryStatus <= 1 ? t('component.order_view.matching_pharmacy') : t('component.order_view.unknown_pharmacy'))}</BringzzText>
                            </div>
                            {order.pharmacy?.telefon && (
                                <div className='flex justify-between mb-2'>
                                    <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.phone')}</BringzzText>
                                    <BringzzText tag='h4'>{order.pharmacy.telefon}</BringzzText>
                                </div>
                            )}
                            {order.customer?.address && (
                                <div className='flex justify-between mb-2'>
                                    <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.address')}</BringzzText>
                                    <BringzzText tag='h4'>{`${order.customer.address.streetName || ''} ${order.customer.address.houseNumber || ''} ${order.customer.address.postalCode || ''} ${order.customer.address.cityName || ''}`}</BringzzText>
                                </div>
                            )}
                            {order.shopper && (
                                <div className='flex justify-between mb-2'>
                                    <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.shopper')}</BringzzText>
                                    <BringzzText tag='h4'>{`${order.shopper.firstName} ${order.shopper.lastName}`}</BringzzText>
                                </div>
                            )}
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.order_date')}</BringzzText>
                                <BringzzText tag='h4'>{order.created || t('component.order_view.not_available')}</BringzzText>
                            </div>
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.order_id')}</BringzzText>
                                <BringzzText tag='h4'>#{order.id}</BringzzText>
                            </div>
                        </div>
                    </div>

                    {order.products && order.products.length > 0 && (
                        <div className='border-t pt-4'>
                            <BringzzText tag='h3' className="mb-2">{t('component.order_view.products')}</BringzzText>
                            <div className='space-y-2'>
                                {order.products.map((product, index) => (
                                    <BringzzBasketProduct
                                        key={product.id || index}
                                        product={{
                                            name: product.name || t('component.order_view.unknown_product'),
                                            quantity: product.quantity || 1,
                                            price: product.price || 0,
                                            amount: product.packageQuantity || product.packaging ? 
                                                `${product.packageQuantity} ${product.packaging}` : 
                                                t('component.order_view.package_details_not_available'),
                                            imgUrl: product.productImage,
                                            manufacturer: product.manufacturer || ''
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {order.fees?.totalFees && (
                        <div className='pt-4'>
                            <BringzzText tag='h3' className="mb-2">{t('component.order_view.fees')}</BringzzText>
                            <div className='bg-gray-50 rounded-lg p-4'>
                                <div className='flex justify-between mb-2'>
                                    <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.total_fees')}</BringzzText>
                                    <BringzzText tag='h4'>€ {order.fees.totalFees}</BringzzText>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='border-t pt-4'>
                        <BringzzText tag='h3' className="mb-2">{t('component.order_view.invoice_details')}</BringzzText>
                        <div className='bg-gray-50 rounded-lg p-4'>
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.invoice_number')}</BringzzText>
                                <BringzzText tag='h4'>#{new Date().getFullYear()}-{invoiceData?.invoiceNumber || 'N/A'}</BringzzText>
                            </div>
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.total_amount')}</BringzzText>
                                <BringzzText tag='h4'>€ {invoiceData?.totalPrice || order.totalPrice || '0.00'}</BringzzText>
                            </div>
                            <div className='flex justify-between mb-2'>
                                <BringzzText tag='h4' className="text-gray-600">{t('component.order_view.products_count')}</BringzzText>
                                <BringzzText tag='h4'>{invoiceData?.products?.length || order.products?.length || 0} {t('component.order_view.items')}</BringzzText>
                            </div>
                            <div className='mt-4'>
                                <BringzzButton
                                    onClick={() => handleDownloadInvoice()}
                                    className="w-full"
                                >
                                    {t('component.order_view.download_invoice_pdf')}
                                </BringzzButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </BringzzPageContainer>
    );
};

export default ConsumerOrderViewPage;
